import { useEffect } from "react";

const BASE_URL = "https://www.businesspal.tech";
const BRAND = "Business Pal";
const OG_IMAGE_DEFAULT = `${BASE_URL}/og-image.jpg`;

export function useSEO({
  title,
  description,
  path,
  keywords,
  imageUrl,
  jsonLd,
  ogType = "website",
}: {
  title: string;
  description: string;
  path: string;
  keywords?: string[];
  imageUrl?: string;
  jsonLd?: object | object[];
  ogType?: string;
}) {
  useEffect(() => {
    const ogImage = imageUrl || OG_IMAGE_DEFAULT;
    document.title = `${title} | ${BRAND}`;

    // Meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute("content", description);

    // Keywords
    if (keywords && keywords.length > 0) {
      let metaKw = document.querySelector('meta[name="keywords"]');
      if (!metaKw) {
        metaKw = document.createElement("meta");
        metaKw.setAttribute("name", "keywords");
        document.head.appendChild(metaKw);
      }
      metaKw.setAttribute("content", keywords.join(", "));
    }

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `${BASE_URL}${path}`);

    // OG + Twitter tags
    const metaTags: Record<string, string> = {
      "og:title": `${title} | ${BRAND}`,
      "og:description": description,
      "og:url": `${BASE_URL}${path}`,
      "og:type": ogType,
      "og:image": ogImage,
      "twitter:title": `${title} | ${BRAND}`,
      "twitter:description": description,
      "twitter:image": ogImage,
    };

    Object.entries(metaTags).forEach(([key, content]) => {
      const isOg = key.startsWith("og:");
      const attr = isOg ? "property" : "name";
      let tag = document.querySelector(`meta[${attr}="${key}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, key);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    });

    // JSON-LD (page-specific)
    const injectedScripts: HTMLScriptElement[] = [];
    if (jsonLd) {
      const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      items.forEach((item) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.dataset.lovableSeo = "page";
        script.text = JSON.stringify(item);
        document.head.appendChild(script);
        injectedScripts.push(script);
      });
    }

    return () => {
      document.title = `${BRAND} - Free Scheduling Software for Every Business`;
      injectedScripts.forEach((s) => s.remove());
    };
  }, [title, description, path, keywords?.join("|"), imageUrl, JSON.stringify(jsonLd), ogType]);
}
